import React, { useState } from 'react';

import { CondensedHamburguerMenu } from '@repo/ui/components/icons/condensed-hamburguer-menu';
import { PersonIcon } from '@repo/ui/components/icons/person-icon';
import { Spinner } from '@repo/ui/components/spinner';
import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { cn } from '@repo/ui/lib/utils';

import { env } from '@env';
import { SignInOptions, useAuth } from '@/hooks/use-auth';
import { useSession } from '@/providers/session-provider';

interface SignInButtonProps {
  onlySignIn?: boolean;
  className?: string;
  /**
   * Surface label propagated to PostHog `signin_popup_opened` (and
   * `login_wall_clicked` when `wallType` is set). Defaults to
   * 'header_iframe'. Walls should pass `wall_${type}_iframe`.
   */
  surface?: string;
  /**
   * Set when this button is rendered inside a login wall — fires
   * `login_wall_clicked` synchronously with the click. The popup-side
   * `signup_initiated` (with provider) joins on distinct_id.
   */
  wallType?: SignInOptions['wallType'];
}

export const SignInButton: React.FC<SignInButtonProps> = ({
  onlySignIn,
  className,
  surface = 'header_iframe',
  wallType,
}) => {
  const { session, isLoading: isLoadingSession } = useSession();
  const { isPending, handleSignIn, handleSignOut } = useAuth();

  const [open, setOpen] = useState(false);

  if (isLoadingSession) {
    return (
      <div className="flex h-auto w-full rounded-[20px] bg-[#E8E8E8] px-3 py-[5px] hover:bg-[#E8E8E8]">
        <Spinner className="mx-auto size-10" />
      </div>
    );
  }

  if (session && !onlySignIn) {
    return (
      <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="flex h-auto w-full rounded-[20px] bg-[#E8E8E8] px-3 py-[5px] text-background hover:bg-[#E8E8E8] hover:text-background 2xl:w-[75%]"
          >
            <div className="flex flex-1 items-center gap-3">
              <PersonIcon className="size-10!" />
              <span className="text-sm font-semibold leading-4">
                {session.user.username || 'Signed in'}
              </span>
            </div>
            <CondensedHamburguerMenu className="h-[19px]! w-auto!" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent className="min-w-[172px] rounded-md border border-[#C0C0C0] bg-[#F3F3F3] p-0">
            <DropdownMenuItem
              disabled={isPending}
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                window.open(
                  `${env.NEXT_PUBLIC_WEBSITE_APP_URL}/delete-account`,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              className="cursor-pointer rounded-b-md rounded-t-none border-none px-[calc(6px+5.33px)] pb-[calc(6px+3.33px)] pt-[calc(4px+3.33px)] focus:bg-[#C0C0C0] cred-md:px-[14px] cred-md:text-sm"
            >
              Manage Account
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={isPending}
              onClick={async (e) => {
                e.preventDefault();
                await handleSignOut();
                setOpen(false);
              }}
              className="cursor-pointer rounded-b-md rounded-t-none border-none px-[calc(6px+5.33px)] pb-[calc(6px+3.33px)] pt-[calc(4px+3.33px)] focus:bg-[#C0C0C0] cred-md:px-[14px] cred-md:text-sm"
            >
              Logout
              {isPending && <Spinner size={5} className="ml-auto" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    );
  }

  return (
    <Button
      variant="ghost"
      disabled={isPending || !!session}
      onClick={(e) =>
        !isPending && !session && handleSignIn(e, { surface, wallType })
      }
      className={cn(
        'relative flex h-auto w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-black px-5 py-2 cred-xl:px-8 cred-xl:py-2.5 text-center text-sm cred-xl:text-[15px] font-semibold text-white no-underline shadow-[0_4px_15px_rgba(41,121,255,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-black/80 hover:text-white hover:shadow-[0_6px_20px_rgba(41,121,255,0.3)]',
        className,
      )}
    >
      {isPending && <Spinner size={5} />}
      Sign in
    </Button>
  );
};
